import { faChevronDown } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Box, Grid, Typography, styled } from '@mui/material';
import { InputOnlyField, Select, Text } from '@ngt/forms';
import { Condition, ConditionMode } from '@ngt/forms-core';
import { FC, ReactNode } from 'react';
import MemberCheck from '../MemberCheck';
import { DietaryRequirementType } from '@/api/dtos';
import SpecialityFields from './SpecialityFields';

const MemeberStaffCheckGrid = styled(Grid)(({theme}) => ({
    width: '100%',
    textAlign: 'left',
    display: 'block',
    paddingTop: 10,
    paddingBottom: 20,

    [theme.breakpoints.up('lg')]: {
        display: 'flex',
        gap: theme.spacing(3),
    },
}))

const GridMember = styled(Grid)(({theme}) => ({
    flex: '1 1 100%'
}))

const columnMapping = {
    xs: 12,
    sm: 6,
    md: 3
}

const countryIsAustralia = ({value}: {value?: {country?: string} | null}) => value?.country?.toLowerCase() === "australia";
const valueSubscription = { value: true };

interface BaseCustomRegistrationSectionProps {
    title: string;
    description?: string;
    additionalFields?: ReactNode;
    StyledAccordion: any;
    StyledAccordianSummary: any;
    StyledAccordionDetails: any;
}

const BaseCustomRegistrationSection: FC<BaseCustomRegistrationSectionProps> = ({
    title,
    description,
    additionalFields,
    StyledAccordion,
    StyledAccordianSummary,
    StyledAccordionDetails
}) => {

    return (
        <StyledAccordion elevation={4}>
            <StyledAccordianSummary 
                expandIcon={<FontAwesomeIcon fixedWidth icon={faChevronDown} color='white' />}
            >
                <Typography variant="h3" color="white"><b>{title}</b></Typography>   
            </StyledAccordianSummary>
            <StyledAccordionDetails>
                <MemeberStaffCheckGrid>
                    <GridMember>
                        <MemberCheck registrationSection={true} />
                    </GridMember>
                </MemeberStaffCheckGrid>

                {description && (
                    <Typography variant='body1' sx={{ paddingBottom: 2 }}>
                        {description}
                    </Typography>
                )}

                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column'
                    }}
                >
                    <Grid
                        container
                        spacing={4}
                    >
                        <Grid  
                            item
                            {...columnMapping}
                        >
                            <InputOnlyField
                                label="Title"
                                propertyPath="title"
                                component={Select}
                                FormControlProps={{
                                    variant: "standard"
                                }}
                                sx={{
                                    marginTop:"16px",
                                    marginBottom:"8px"
                                }}
                            />
                        </Grid>
                        <Grid  
                            item
                            {...columnMapping}
                        >
                            <InputOnlyField
                                label="First Name"
                                propertyPath="firstName"
                                component={Text}
                                variant="standard"
                                margin="normal"
                            />
                        </Grid>
                        <Grid  
                            item
                            {...columnMapping}
                        >
                            <InputOnlyField
                                label="Last Name"
                                propertyPath="lastName"
                                component={Text}
                                variant="standard"
                                margin="normal"
                            />
                        </Grid>
                        <Grid  
                            item
                            {...columnMapping}
                        >
                            <InputOnlyField
                                label="Email"
                                propertyPath="email"
                                component={Text}
                                variant="standard"
                                margin="normal"
                            />
                        </Grid>
                        <Grid
                            item
                            {...columnMapping}
                        >
                            <InputOnlyField
                                label="Mobile Phone Number"
                                propertyPath="mobile"
                                component={Text}
                                variant="standard"
                                margin="normal"
                            />
                        </Grid>
                        <Grid
                            item
                            marginTop='16px'
                            {...columnMapping}
                        >
                            <InputOnlyField
                                propertyPath="dietaryRequirement"
                                label="Dietary Requirement"
                                component={Select}
                                FormControlProps={{
                                    variant: "standard"
                                }}
                            />
                        </Grid>

                        <Condition
                            condition={({value}) => value?.dietaryRequirement === DietaryRequirementType.SpecificFoodAllergy}
                            subscription={{
                                value: true
                            }}
                            mode={ConditionMode.Enable}
                        >
                            <Grid
                                item
                                {...columnMapping}
                            >
                                <InputOnlyField
                                    label="Dietary Requirements"
                                    propertyPath="dietaryRequirements"
                                    component={Text}
                                    variant="standard"
                                    margin="normal"
                                />
                            </Grid>
                        </Condition>
                        <Grid
                            item
                            xs={12}
                            sm={6}
                            md={6}
                        >
                            <InputOnlyField
                                label="Job Title"
                                propertyPath="jobDescription"
                                component={Text}
                                variant="standard"
                                margin="normal"
                            />
                        </Grid>
                        <SpecialityFields />
                        <Grid  
                            item
                            sm={12}
                        >
                            <Typography variant='h2'>
                                Institution
                            </Typography>
                        </Grid>

                        <Grid  
                            item
                            {...columnMapping}
                        >
                            <InputOnlyField
                                label="Associated Institution"
                                propertyPath="institution"
                                component={Text}
                                variant="standard"
                                margin="normal"
                            />
                        </Grid>
                        <Grid  
                            item
                            {...columnMapping}
                        >
                            <InputOnlyField
                                label="Institution Address"
                                propertyPath="address"
                                component={Text}
                                variant="standard"
                                margin="normal"
                            />
                        </Grid>
                        <Grid  
                            item
                            {...columnMapping}
                        >
                            <InputOnlyField
                                label="Institution Suburb"
                                propertyPath="suburb"
                                component={Text}
                                variant="standard"
                                margin="normal"
                            />
                        </Grid>
                        <Grid  
                            item
                            {...columnMapping}
                        >
                            <InputOnlyField
                                label="Institution Postcode"
                                propertyPath="postcode"
                                component={Text}
                                variant="standard"
                                margin="normal"
                            />
                        </Grid>
                        <Grid  
                            item
                            {...columnMapping}
                        >
                            <InputOnlyField
                                label="Institution Country"
                                propertyPath="country"
                                component={Text}
                                variant="standard"
                                margin="normal"
                            />
                        </Grid>
                        <Grid
                            item
                            {...columnMapping}
                        >
                            <Condition 
                                condition={countryIsAustralia}
                                subscription={valueSubscription}
                                mode={ConditionMode.Enable}
                            >
                                <InputOnlyField
                                    label="State"
                                    propertyPath="state"
                                    component={Select}
                                    FormControlProps={{
                                        variant: "standard"
                                    }}
                                    sx={{
                                        marginTop:"16px",
                                        marginBottom:"8px"
                                    }}
                                />
                            </Condition>
                        </Grid>
                        {additionalFields}
                    </Grid>
                </Box>
            </StyledAccordionDetails>
        </StyledAccordion>
    );
}

export default BaseCustomRegistrationSection;
